import { useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FormApi } from 'final-form';
import { Box } from '@mui/material';
import { Paths } from 'type-fest';

import { HelpTip, Typography, LoadingOverlay } from '@occc/ui-design-system';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';
import OfferTypeSelectField from '@occc-app/routes/CampaignComponents/components/OfferTypeSelect';
import {
  promotionTypes,
  requiresActivationApplicablePromotionTypes,
} from '@occc-app/routes/Promotion/constants';
import { ProductDiscountConfiguration } from '@occc-app/routes/Promotion/components/ProductDiscountConfiguration';
import {
  TRANSACTION_DISCOUNT_FORM_PREFIX,
  TransactionDiscountConfiguration,
} from '@occc-app/routes/Promotion/components/TransactionDiscountConfiguration';
import { BuyAndGetConfiguration } from '@occc-app/routes/Promotion/components/BuyAndGetConfiguration';
import { validateConfiguration } from '@occc-app/routes/Promotion/utils';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import {
  FulfillmentChannelConstraint,
  FulfillmentWindow,
  MinimumPurchaseConstraints,
  PaymentMethodConstraint,
  RequiresActivationConstraintWithLimits,
  Stackable,
} from '@occc/feat-constraints';
import { AwardCount } from '@occc-app/routes/CampaignComponents/components/AwardCount';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { EitherActivationOrAwardCount } from '@occc-app/routes/Promotion/components/PromotionConfigureForm/EitherActivationOrAwardCountProps';
import { useChainConfiguration, useRollbackStackingConfig } from '@occc-app/Chain/hooks';
import { useConfig } from '@occc/config';
import { User_Role } from '@occc/graphql';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { useUserSelector } from '@occc-app/App/AppContext';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';
import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { OfferCampaignField } from '@occc-app/routes/Offer/components/OfferCampaignField/offer-campaign-field.component';
import { OfferFulfillmentPriorityField } from '@occc-app/offerHelpers/components/OfferFulfillmentPriorityField';
import {
  PromotionConfigureFormInitialValues,
  PromotionConfigureFormValues,
  TransactionDiscountAwardType,
  PromotionType,
} from '../../types';
import { VarietyDiscountConfiguration } from '../VarietyDiscountConfiguration';
import { FuelRollbackConfiguration } from '../FuelRollbackConfiguration';
import { CustomData } from '../../../CampaignComponents/components/CustomData/CustomData';
import { configureFormCustomDataSubtitle } from '../../../../offerHelpers/copies';
import { ScanDataConfiguration } from '../../../CampaignComponents/components/ScanDataConfiguration/ScanDataConfiguration';

type ConfigureFormProps = {
  onSubmit: (
    values: PromotionConfigureFormValues,
    form: FormApi<PromotionConfigureFormValues, PromotionConfigureFormInitialValues>,
  ) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: PromotionConfigureFormInitialValues;
  loading: boolean;
  currentFlowType: Flow_Types;
  isFormEditable: boolean;
  onNextStepButtonClick?: () => void;
};

export const ConfigureForm = ({
  onSubmit,
  initialValues,
  onCancelClick,
  loading,
  currentFlowType,
  isFormEditable,
  onNextStepButtonClick,
}: ConfigureFormProps) => {
  const rollbackStackingConfig = useRollbackStackingConfig();
  const { allowMultipleTransactionDiscounts } = useChainConfiguration();
  const isOrderAheadEnabled = useConfig('orderAheadConfig').enabled;
  const user = useUserSelector();
  const isEditCampaignEnabled = isFormEditable && user?.userRole === User_Role.Admin;

  const renderForm = useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      dirty,
      form,
    }: FormRenderProps<PromotionConfigureFormValues, PromotionConfigureFormInitialValues>) => {
      const handleCancelClick = () => {
        onCancelClick(dirty);
      };

      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          currentFlowType={currentFlowType}
          onCancel={handleCancelClick}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={handleCancelClick}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      const isAllowMultipleFulfillmentsPerProductGroupThresholdEnabled =
        values.offerType === PromotionType.TransactionDiscount &&
        allowMultipleTransactionDiscounts &&
        values.transactionDiscount?.awardType === TransactionDiscountAwardType.MoneyDiscount;

      const allowMultipleFulfillmentsPerProductGroupThresholdFieldName =
        `${TRANSACTION_DISCOUNT_FORM_PREFIX}.allowMultipleFulfillmentsPerProductGroupThreshold` satisfies Paths<PromotionConfigureFormValues>;

      return (
        <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmit}>
          <OfferEarningMechanismConfigurationSection>
            <OfferTypeSelectField
              isChangeButtonVisible={isFormEditable}
              items={promotionTypes}
              name='offerType'
            />
            {values.offerType && (
              <>
                <Box mb={2}>
                  <OfferNameAndDescriptionConfiguration entityName='Promotion'>
                    {currentFlowType === Flow_Types.Update && (
                      <OfferCampaignField canEdit={isEditCampaignEnabled} />
                    )}
                  </OfferNameAndDescriptionConfiguration>
                  <OfferFulfillmentPriorityField mt={2} />
                </Box>
                {values.offerType === PromotionType.ProductDiscount && (
                  <ProductDiscountConfiguration values={values.productDiscount} />
                )}
                {values.offerType === PromotionType.TransactionDiscount && (
                  <TransactionDiscountConfiguration values={values.transactionDiscount} />
                )}
                {values.offerType === PromotionType.BuyAndGet && <BuyAndGetConfiguration />}
                {values.offerType === PromotionType.VarietyDiscount && (
                  <VarietyDiscountConfiguration values={values.varietyDiscount} />
                )}
                {values.offerType === PromotionType.FuelRollback && <FuelRollbackConfiguration />}
              </>
            )}
          </OfferEarningMechanismConfigurationSection>

          {values.offerType && (
            <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
              {requiresActivationApplicablePromotionTypes.includes(values.offerType) && (
                <EitherActivationOrAwardCount form={form} />
              )}

              <OfferConfigurationSection title='Limits'>
                {[
                  PromotionType.ProductDiscount,
                  PromotionType.TransactionDiscount,
                  PromotionType.VarietyDiscount,
                  PromotionType.BuyAndGet,
                  PromotionType.FuelRollback,
                ].includes(values.offerType) && (
                  <Box>
                    <AwardCount
                      values={values}
                      stacked
                      shouldShowTransactionLimit
                      shouldShowTransactionFrequencyLimit
                      defaultPeriod='DAY'
                    />
                    <Box p={3} pt={0}>
                      <HelpTip title='Promotional discount fulfillments'>
                        <Typography variant='body2' color='textSecondary'>
                          Promotional discount fulfillments are incremented upon the successful
                          finalization of a loyalty transaction, as such they may have a slight risk
                          of over-running if multiple customers are transacting simultaneously as
                          the limit is reached.
                        </Typography>
                      </HelpTip>
                    </Box>
                  </Box>
                )}
              </OfferConfigurationSection>

              <OfferConfigurationSection title='Requirements'>
                {[PromotionType.FuelRollback].includes(values.offerType) && (
                  <Stackable disabled={['OFF', 'ALL'].includes(rollbackStackingConfig)} />
                )}

                {[
                  PromotionType.ProductDiscount,
                  PromotionType.TransactionDiscount,
                  PromotionType.VarietyDiscount,
                  PromotionType.BuyAndGet,
                  PromotionType.FuelRollback,
                ].includes(values.offerType) && <PaymentMethodConstraint values={values} />}

                {[
                  PromotionType.ProductDiscount,
                  PromotionType.TransactionDiscount,
                  PromotionType.VarietyDiscount,
                  PromotionType.BuyAndGet,
                ].includes(values.offerType) && (
                  <MinimumPurchaseConstraints
                    values={values}
                    allowMultipleFulfillmentsPerProductGroupThreshold={{
                      fieldName: allowMultipleFulfillmentsPerProductGroupThresholdFieldName,
                      isEnabled: isAllowMultipleFulfillmentsPerProductGroupThresholdEnabled,
                    }}
                  />
                )}

                {requiresActivationApplicablePromotionTypes.includes(values.offerType) && (
                  <RequiresActivationConstraintWithLimits values={values.activation} />
                )}

                {[
                  PromotionType.ProductDiscount,
                  PromotionType.TransactionDiscount,
                  PromotionType.VarietyDiscount,
                  PromotionType.BuyAndGet,
                ].includes(values.offerType) && <FulfillmentWindow values={values} />}

                {isOrderAheadEnabled &&
                  [
                    PromotionType.ProductDiscount,
                    PromotionType.TransactionDiscount,
                    PromotionType.VarietyDiscount,
                    PromotionType.BuyAndGet,
                  ].includes(values.offerType) && (
                    <FulfillmentChannelConstraint values={values.fulfillmentChannelConstraint} />
                  )}
              </OfferConfigurationSection>

              <OfferConfigurationSection title='Advanced Offer Settings' mb={0}>
                {[
                  PromotionType.ProductDiscount,
                  PromotionType.TransactionDiscount,
                  PromotionType.VarietyDiscount,
                  PromotionType.BuyAndGet,
                  PromotionType.FuelRollback,
                ].includes(values.offerType) && (
                  <>
                    <CustomData
                      values={values.customData}
                      subtitle={configureFormCustomDataSubtitle}
                    />
                    <ScanDataConfiguration
                      values={values.scanDataConfiguration}
                      submitting={submitting}
                    />
                  </>
                )}
              </OfferConfigurationSection>
            </OfferConfigurationWizardFormLayout>
          )}
        </OfferForm>
      );
    },
    [
      isFormEditable,
      currentFlowType,
      onNextStepButtonClick,
      rollbackStackingConfig,
      onCancelClick,
      allowMultipleTransactionDiscounts,
      isOrderAheadEnabled,
      isEditCampaignEnabled,
    ],
  );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isFormEditable}>
        <Form<PromotionConfigureFormValues, PromotionConfigureFormInitialValues>
          onSubmit={onSubmit}
          render={renderForm}
          mutators={{
            ...arrayMutators,
            setFieldValue: (args, state, utils) => {
              // name, value
              utils.changeValue(state, args[0], () => args[1]);
            },
          }}
          initialValues={initialValues}
          validate={validateConfiguration}
        />
      </CanEditFormFields>
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
